import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { EditAdForm } from '@/components/AdForm/EditAdForm';
import {
  EditAdError,
  EditAdFormFields,
} from '@/components/AdForm/AdForm.types';
import { useServerDataContext } from '@/contexts';
import { AdFormLayout } from '@/components/AdForm/components/AdFormLayout';
import { getInitialValuesForEdit } from '@/components/AdForm/AdForm.helpers';
import { useAuthStore } from '@/store/useAuthStore';
import { EditAdNoAccessView } from '@/views/Ads/EditAdNoAccess.view';
import { BannedView } from '@/views/Ads/Banned.view';
import { ErrorCodesEnum } from '@/api/types/common.types';

export const EditAdView = () => {
  const { t } = useTranslation();
  const { initialAdDetails } = useServerDataContext();
  const defaultValues = getInitialValuesForEdit(initialAdDetails);
  const [isAuthLoaded, userId, isBanned] = useAuthStore(state => [
    state.isAuthLoaded,
    state.userInfo?.id,
    state.userInfo?.isBanned,
  ]);
  const [isBannedView, setBannedView] = useState(isBanned);
  const isAuthor = initialAdDetails?.user?.id === userId;

  const methods = useForm<EditAdFormFields>({
    defaultValues,
  });

  useEffect(() => {
    setBannedView(isBanned);
  }, [isBanned]);

  function onSubmitError(errors: EditAdError[]) {
    const hasUserBannedError = errors.find(
      error => error.extensions?.code === ErrorCodesEnum.UserBannedError
    );

    if (hasUserBannedError) {
      setBannedView(true);
    }
  }

  if (!isAuthLoaded) return null;

  if (!isAuthor) {
    return <EditAdNoAccessView />;
  }

  if (isBannedView) {
    return <BannedView description={t('pages.edit_ad.banned_message')} />;
  }

  return (
    <FormProvider {...methods}>
      <AdFormLayout mode="edit" appearance="form">
        <EditAdForm onSubmitError={onSubmitError} />
      </AdFormLayout>
    </FormProvider>
  );
};
